/**
 * Thread palette for embroidery decoration.
 *
 * Shared because three places need the same list: the API derives the initial
 * per-color suggestion from it, the modal renders it as dropdown options, and
 * the client-side stitch renderer paints with it.
 */

export interface ThreadColor {
  id: string;
  name: string;
  hex: string;
  /** Metallic threads are excluded from automatic nearest-color matching. */
  metallic?: boolean;
}

/**
 * PROVISIONAL palette — 15 colors taken from the Justdroppit design prototype.
 *
 * Replaced 1:1 by the binding supplier list; nothing outside this file changes.
 * Note this is the palette SIZE (what a seller can pick from), not the per-design
 * ceiling — that is `DecorationMethod.maxColors`, which is 6.
 */
export const THREAD_COLORS: ThreadColor[] = [
  { id: 'white', name: 'White', hex: '#f4f4f2' },
  { id: 'black', name: 'Black', hex: '#141414' },
  { id: 'grey', name: 'Grey', hex: '#8b8b8b' },
  { id: 'beige', name: 'Beige', hex: '#d8c6a5' },
  { id: 'yellow', name: 'Yellow', hex: '#f4d01c' },
  { id: 'gold', name: 'Gold (metallic)', hex: '#c9a227', metallic: true },
  { id: 'silver', name: 'Silver (metallic)', hex: '#b9bec5', metallic: true },
  { id: 'orange', name: 'Orange', hex: '#f07a1a' },
  { id: 'signal-red', name: 'Signal Red', hex: '#e3261b' },
  { id: 'dark-red', name: 'Dark Red', hex: '#8d1b24' },
  { id: 'pink', name: 'Pink', hex: '#f19ec2' },
  { id: 'violet', name: 'Violet', hex: '#7a3fa0' },
  { id: 'blue', name: 'Blue', hex: '#2456d6' },
  { id: 'dark-blue', name: 'Dark Blue', hex: '#1d2a5c' },
  { id: 'green', name: 'Green', hex: '#2f8f46' },
];

/** Fallback per-design thread ceiling when a decoration method carries no `maxColors`. */
export const DEFAULT_MAX_THREAD_COLORS = 6;

/**
 * Widest an embroidered motif may be, in millimetres — a production limit of
 * the embroidery machines, not of any product's field. A field wider than
 * this still stitches motifs of at most this width (product decision,
 * 2026-09-04).
 */
export const MAX_EMBROIDERY_WIDTH_MM = 90;

/** Narrowest an embroidered motif may be, in millimetres (product decision, 2026-09-04). */
export const MIN_EMBROIDERY_WIDTH_MM = 50;

/** Maximum palette entries the analysis returns, regardless of the ceiling. */
export const MAX_PALETTE_ENTRIES = 12;

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16) || 0,
    g: parseInt(clean.slice(2, 4), 16) || 0,
    b: parseInt(clean.slice(4, 6), 16) || 0,
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')
  );
}

/** Squared euclidean RGB distance — square root omitted, only ordering matters. */
export function rgbDistanceSq(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number }
): number {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return dr * dr + dg * dg + db * db;
}

/**
 * Nearest thread by RGB distance. Metallic threads are skipped: their appearance
 * comes from the light gradient at render time, not from their flat hex, so
 * matching against that hex would suggest them for the wrong colors.
 */
export function nearestThread(
  color: { r: number; g: number; b: number },
  threads: ThreadColor[] = THREAD_COLORS
): ThreadColor {
  const candidates = threads.filter((t) => !t.metallic);
  const pool = candidates.length > 0 ? candidates : threads;

  let best = pool[0];
  let bestDistance = Infinity;
  for (const thread of pool) {
    const distance = rgbDistanceSq(color, hexToRgb(thread.hex));
    if (distance < bestDistance) {
      bestDistance = distance;
      best = thread;
    }
  }
  return best;
}

export function threadById(
  id: string,
  threads: ThreadColor[] = THREAD_COLORS
): ThreadColor | undefined {
  return threads.find((t) => t.id === id);
}
